import React, { useState, useEffect, useCallback } from "react";
import { Button, Spinner } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import {
  Trophy, XCircle, Clock, CheckCircle, MinusCircle, ArrowLeft, RotateCcw,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import "./StudentAssessments.scss";

const formatDuration = (seconds) => {
  if (!seconds) return "—";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

// ── Review Question Component ────────────────────────────────────────
const ReviewQuestion = ({ question, answer, index, showCorrect, showExplanation }) => {
  const getOptionClass = (opt) => {
    if (!showCorrect) {
      const isSelected = (answer?.selectedOptionIds || []).map(String).includes(opt._id?.toString());
      return isSelected ? "review-question__option--wrong" : "review-question__option--neutral";
    }
    const isSelected = (answer?.selectedOptionIds || []).map(String).includes(opt._id?.toString());
    const isCorrect = opt.isCorrect;

    if (isCorrect) return "review-question__option--correct";
    if (isSelected && !isCorrect) return "review-question__option--wrong";
    return "review-question__option--neutral";
  };

  const resultClass = answer?.isCorrect
    ? "review-question__result--correct"
    : answer?.isAnswered
      ? "review-question__result--incorrect"
      : "review-question__result--skipped";

  const resultLabel = answer?.isCorrect
    ? "Correct"
    : answer?.isAnswered
      ? "Incorrect"
      : "Skipped";

  return (
    <div className="review-question">
      <div className="review-question__header">
        <span className="review-question__number">Q{index + 1} · {question.marks || 1} mark(s)</span>
        <span className={`review-question__result ${resultClass}`}>{resultLabel}</span>
      </div>

      <div className="review-question__text">{question.questionText}</div>

      {question.imageUrl && <img src={question.imageUrl} alt="" className="ta-question__image" />}
      {question.codeSnippet && <pre className="ta-question__code">{question.codeSnippet}</pre>}

      {question.options?.length > 0 && question.options.map((opt) => (
        <div key={opt._id} className={`review-question__option ${getOptionClass(opt)}`}>
          {opt.text}
        </div>
      ))}

      {["text_answer", "fill_blank"].includes(question.questionType) && (
        <div className="review-question__option review-question__option--neutral">
          Your answer: {answer?.textAnswer || "(empty)"}
        </div>
      )}

      {showExplanation && question.explanation && (
        <div className="review-question__explanation">
          <strong>Explanation:</strong> {question.explanation}
        </div>
      )}
    </div>
  );
};

// ── Main Result Page ─────────────────────────────────────────────────
const AssessmentResultPage = () => {
  const { attemptId } = useParams();
  const navigate = useNavigate();

  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchResult = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/student-assessments/attempt/${attemptId}/result`);
      setResult(data.result);
    } catch {
      // handled silently
    } finally {
      setLoading(false);
    }
  }, [attemptId]);

  useEffect(() => { fetchResult(); }, [fetchResult]);

  if (loading) {
    return <div className="text-center py-5"><Spinner animation="border" size="sm" /></div>;
  }

  if (!result) {
    return <div className="text-center py-5 text-muted">Result not available</div>;
  }

  const a = result.assessment || {};
  const passed = result.passed;

  return (
    <div className="assessment-result-page">
      <Button variant="link" className="mb-3 p-0 text-decoration-none" onClick={() => navigate("/s/assessments")}>
        <ArrowLeft size={16} className="me-1" /> Back to Assessments
      </Button>

      {/* Hero */}
      <div className="result-hero">
        <div className={`result-hero__icon ${passed ? "result-hero__icon--passed" : "result-hero__icon--failed"}`}>
          {passed ? <Trophy size={32} /> : <XCircle size={32} />}
        </div>
        <div className="result-hero__score">{result.percentage}%</div>
        <div className="result-hero__label">
          {result.score}/{result.totalMarks} marks · Attempt #{result.attemptNumber}
        </div>
        {a.passingPercentage > 0 && (
          <span className={`result-hero__status ${passed ? "result-hero__status--passed" : "result-hero__status--failed"}`}>
            {passed ? "Passed" : "Failed"}
          </span>
        )}
        <div className="mt-2 text-muted small">
          {result.status === "auto_submitted" && "⚠ Auto-submitted due to violations"}
          {result.status === "expired" && "⚠ Time expired"}
        </div>
      </div>

      {/* Stats */}
      <div className="result-stats">
        <div className="result-stat">
          <div className="result-stat__value d-flex align-items-center justify-content-center gap-1">
            <CheckCircle size={16} className="text-success" /> {result.correctCount}
          </div>
          <div className="result-stat__label">Correct</div>
        </div>
        <div className="result-stat">
          <div className="result-stat__value d-flex align-items-center justify-content-center gap-1">
            <XCircle size={16} className="text-danger" /> {result.incorrectCount}
          </div>
          <div className="result-stat__label">Incorrect</div>
        </div>
        <div className="result-stat">
          <div className="result-stat__value d-flex align-items-center justify-content-center gap-1">
            <MinusCircle size={16} /> {result.unansweredCount}
          </div>
          <div className="result-stat__label">Skipped</div>
        </div>
        <div className="result-stat">
          <div className="result-stat__value d-flex align-items-center justify-content-center gap-1">
            <Clock size={16} /> {formatDuration(result.timeSpent)}
          </div>
          <div className="result-stat__label">Time Spent</div>
        </div>
      </div>

      {/* Actions */}
      <div className="d-flex gap-2 mb-3 justify-content-center">
        {a._id && (
          <Button variant="outline-primary" size="sm" onClick={() => navigate(`/s/assessment/${a._id}`)}>
            <RotateCcw size={14} className="me-1" /> Try Again
          </Button>
        )}
      </div>

      {/* Review */}
      {result.questions?.length > 0 && (
        <div className="result-review">
          <div className="result-review__title">Question Review</div>
          {result.questions.map((q, idx) => {
            const answer = result.answers?.find(
              (ans) => ans.question?.toString() === q._id?.toString()
            );
            return (
              <ReviewQuestion
                key={q._id}
                question={q}
                answer={answer}
                index={idx}
                showCorrect={a.showCorrectAnswers}
                showExplanation={a.showExplanation}
              />
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AssessmentResultPage;
